import { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { PROFILE_LINKS_QUERY_STRING } from 'shared/constants/QUERY_STRING';
import { profileDao } from 'shared/dao/profileDao';
import {
  ILinkProfiles,
  IMultipleProfilePayload,
  INewProfileMatchReponsePayload,
  IProfileCreatePayload,
  IProfileCreateResponsePayload,
  IProfileGraphQLResponse,
  IProfileMatchReponsePayload,
  IProfileMediaSetSelectPayload,
  IProfilePrimaryImageResponsePayload,
  IProfileResponsePayload,
  IProfilesGraphQL,
  IProfileTabDetailReponsePayload,
  IProfileUpdatePayload,
  ISingleProfileResponsePayload,
} from 'shared/interfaces/IProfile';
import { IErrorResponse } from 'shared/interfaces/utils/IErrorResonse';
import { IMediaFileType } from 'shared/interfaces/utils/IMediaFileType';

const {
  getProfiles: getProfilesDao,
  getProfilesGraphQL: getProfilesGraphQLDao,
  getSingleProfile: getSingleProfileDao,
  getProfileTabDetail: getProfileTabDetailDao,
  createProfile: createProfileDao,
  updateProfile: updateProfileDao,
  getProfilePrimaryImage: getProfilePrimaryImageDao,
  setProfilePrimaryImage: setProfilePrimaryImageDao,
  getProfileMedia: getProfileMediaDao,
  setSelectProfileMedia: setSelectProfileMediaDao,
  unSelectProfileMedia: unSelectProfileMediaDao,
  updateProfileMediaSort: updateProfileMediaSortDao,
  getProfileMatch: getProfileMatchDao,
  claimGhostedProfile: claimGhostedProfileDao,
  createMultipleProfiles: createMultipleProfileDao,
  archiveProfile: archiveProfilesDao,
  getSingleProfileGraphQL: getSingleProfileGraphQLDao,
  getProfileLinks: getProfileLinksDao,
} = profileDao();

export const profileService = () => {
  const getProfiles = () => {
    return useQuery<IProfileResponsePayload, Error>(['profiles'], () => getProfilesDao());
  };

  const getProfilesGraphQL = (searchString: string) => {
    return useQuery<IProfilesGraphQL, Error>(['profiles', searchString], () => getProfilesGraphQLDao(searchString));
  };

  const getProfileLinks = () => {
    return useQuery<ILinkProfiles, Error>([PROFILE_LINKS_QUERY_STRING.PROFILE_LIST], () => getProfileLinksDao());
  };

  const getSingleProfile = (id: string) => {
    const enabled = typeof id === 'string';
    return useQuery<ISingleProfileResponsePayload, Error>(['profile', id], () => getSingleProfileDao(id), { enabled });
  };

  const getSingleProfileGraphQL = (id: string) => {
    const enabled = typeof id === 'string';
    return useQuery<IProfileGraphQLResponse, Error>(['profile', id], () => getSingleProfileGraphQLDao(id), { enabled });
  };

  const getProfileTabDetail = (id: string, tab: string) => {
    const enabled = typeof id === 'string';
    return useQuery<IProfileTabDetailReponsePayload, AxiosError<IErrorResponse>>(
      ['profile_tab', id, tab],
      () => getProfileTabDetailDao(id, tab),
      { enabled: enabled },
    );
  };

  const createProfile = () => {
    return useMutation<IProfileCreateResponsePayload, AxiosError<IErrorResponse>, IProfileCreatePayload>(
      (payload: IProfileCreatePayload) => createProfileDao(payload),
    );
  };

  const createMultipleProfiles = () => {
    return useMutation<IProfileMatchReponsePayload, AxiosError<IErrorResponse>, IMultipleProfilePayload>(
      (payload: IMultipleProfilePayload) => createMultipleProfileDao(payload),
    );
  };

  const updateProfile = () => {
    return useMutation(({ profileId, payload }: { profileId: string; payload: IProfileUpdatePayload }) =>
      updateProfileDao(profileId, payload),
    );
  };

  const getProfilePrimaryImage = (profileId: string) => {
    return useQuery<IProfilePrimaryImageResponsePayload>(['profile_primary_image', profileId], () =>
      getProfilePrimaryImageDao(profileId),
    );
  };

  const setProfilePrimaryImage = () => {
    return useMutation<
      IProfilePrimaryImageResponsePayload,
      AxiosError<IErrorResponse>,
      {
        profileId: string;
        formData: FormData;
        onProgress?: (current: number) => void;
      }
    >(
      ({
        profileId,
        formData,
        onProgress,
      }: {
        profileId: string;
        formData: FormData;
        onProgress?: (current: number) => void;
      }) => setProfilePrimaryImageDao(profileId, formData, onProgress),
    );
  };

  const getMediaProfile = (profileId: string, fileType: IMediaFileType) => {
    return useQuery(['profile_media', profileId, fileType], () => getProfileMediaDao(profileId, fileType), {
      enabled: !!profileId,
    });
  };

  const setSelectProfileMedia = () => {
    return useMutation<
      IProfileResponsePayload,
      AxiosError<IErrorResponse>,
      { profileId: string; payload: IProfileMediaSetSelectPayload }
    >(({ profileId, payload }: { profileId: string; payload: IProfileMediaSetSelectPayload }) =>
      setSelectProfileMediaDao(profileId, payload),
    );
  };

  const unSelectProfileMedia = () => {
    return useMutation<
      IProfileResponsePayload,
      AxiosError<IErrorResponse>,
      { profileId: string; profileMediaId: string }
    >(({ profileId, profileMediaId }: { profileId: string; profileMediaId: string }) =>
      unSelectProfileMediaDao(profileId, profileMediaId),
    );
  };

  const updateProfileMediaSort = () => {
    return useMutation(({ profileId, sort }: { profileId: string; sort: { id: string; sort: number }[] }) =>
      updateProfileMediaSortDao(profileId, sort),
    );
  };

  const getProfileMatch = (ghosted: boolean, first_name: string, last_name: string, project_slug?: string) => {
    return useQuery<INewProfileMatchReponsePayload, Error>(
      ['profiles/match', ghosted, first_name, last_name, project_slug],
      () => getProfileMatchDao(ghosted, first_name, last_name, project_slug),
      // {
      //   enabled: !!first_name && !!last_name,
      // },
    );
  };

  const claimGhostedProfile = () => {
    return useMutation(
      ({
        profileId,
        medium_id,
        user_id,
        is_guardian,
      }: {
        profileId: string[];
        medium_id?: string;
        user_id?: string;
        is_guardian?: boolean;
      }) => claimGhostedProfileDao(profileId, medium_id, user_id, is_guardian),
    );
  };

  const archiveProfile = () => {
    return useMutation<IProfileResponsePayload, Error, string>((profileId) => archiveProfilesDao(profileId));
  };

  return {
    archiveProfile,
    createMultipleProfiles,
    getProfiles,
    getProfilesGraphQL,
    getSingleProfile,
    getSingleProfileGraphQL,
    getProfileTabDetail,
    createProfile,
    updateProfile,
    getProfilePrimaryImage,
    setProfilePrimaryImage,
    getMediaProfile,
    setSelectProfileMedia,
    unSelectProfileMedia,
    updateProfileMediaSort,
    getProfileMatch,
    claimGhostedProfile,
    getProfileLinks,
  };
};
